import axios from "axios";

const API_URL = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
const GET_STEP_URL = `${API_URL}/api/wix/oauth/getStep`;
const MARKETPLACE_HEADER = "X-Marketplace";
// const SUPPORTED_MARKETPLACES = new Set(["EBAY", "ETSY", "AMAZON"]);

export const getSelectedMarketplace = () => {
  // Marketplace switching is disabled; all frontend API requests are eBay-scoped.
  return "EBAY";
  // const stored = (sessionStorage.getItem("marketplace") || "EBAY").toUpperCase();
  // return SUPPORTED_MARKETPLACES.has(stored) ? stored : "EBAY";
};

export const getMarketplaceHeaders = () => ({
  [MARKETPLACE_HEADER]: getSelectedMarketplace(),
});

const attachMarketplaceHeader = (config) => {
  config.headers = config.headers || {};
  if (!config.headers[MARKETPLACE_HEADER]) {
    config.headers[MARKETPLACE_HEADER] = getSelectedMarketplace();
  }
  return config;
};

// ===============
// Global Loading Handler
// ===============
let globalHandler = null;

export const setLoadingHandler = (handler) => {
  globalHandler = handler;
};

// ===============
// Create Axios Instance
// ===============
const instance = axios.create({
  baseURL: `${API_URL}/api`, // ✅ Base API path (No /api/api duplication)
});

// Keep onboarding resolution on the canonical backend URL. Passing the
// absolute URL also makes the requested endpoint unambiguous in the browser:
// https://api.ecstacommerce.com/api/wix/oauth/getStep
// A single retry handles the first-load race while the backend/session is
// becoming available. Callers still handle the second failure normally.
export const getCurrentStep = () =>
  instance.get(GET_STEP_URL, { __skipAuthRedirect: true }).catch((firstError) => new Promise((resolve, reject) => {
    window.setTimeout(() => {
      instance.get(GET_STEP_URL, { __skipAuthRedirect: true }).then(resolve).catch((secondError) => {
        secondError.firstGetStepError = firstError;
        reject(secondError);
      });
    }, 250);
  }));

// ===============
// Request Interceptor → Show Loading + Attach Token
// ===============
instance.interceptors.request.use(
  (config) => {
    attachMarketplaceHeader(config);
    const token = sessionStorage.getItem("accessToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // ✅ Trigger global loading before request
    if (globalHandler && !config.__skipLoading) {
      globalHandler.showLoading();
    }

    return config;
  },
  (error) => {
    if (globalHandler) globalHandler.hideLoading();
    return Promise.reject(error);
  }
);

// Keep legacy modules that use the default Axios client consistent too.
axios.interceptors.request.use(attachMarketplaceHeader);

// ===============
// Refresh Token Queue Setup (for parallel 401 requests)
// ===============
let isRefreshing = false;
let refreshQueue = [];

const subscribeToRefresh = (cb) => refreshQueue.push(cb);

const resolveQueue = (token) => {
  refreshQueue.forEach((cb) => cb(token));
  refreshQueue = [];
};

// ===============
// Response Interceptor → Hide Loading + Refresh Token on 401
// ===============
instance.interceptors.response.use(
  (response) => {
    if (globalHandler) globalHandler.hideLoading(); // ✅ Stop loading after success
    return response;
  },

  async (error) => {
    if (globalHandler) globalHandler.hideLoading(); // ✅ Stop loading on error

    const originalRequest = error.config;

    // -------- 401 → Try Refresh Token --------
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = sessionStorage.getItem("refreshToken");
      if (!refreshToken) {
        if (originalRequest.__skipAuthRedirect) {
          return Promise.reject(error);
        }
        sessionStorage.clear();
        window.location.replace("/ebay/unauthorized");
        return Promise.reject(error);
      }

      if (!isRefreshing) {
        isRefreshing = true;

        try {
          const response = await axios.post(`${API_URL}/api/wix/oauth/refresh-token`, {
            refreshToken,
          });

          const newAccessToken = response.data.accessToken;
          sessionStorage.setItem("accessToken", newAccessToken);

          isRefreshing = false;
          resolveQueue(newAccessToken);

          // ✅ Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return instance(originalRequest);

        } catch (err) {
          console.error("Refresh token failed:", err);
          isRefreshing = false;
          if (originalRequest.__skipAuthRedirect) {
            return Promise.reject(err);
          }
          sessionStorage.clear();
         window.location.replace("/ebay/unauthorized");
          return Promise.reject(err);
        }
      }

      // -------- If already refreshing → queue request --------
      return new Promise((resolve) => {
        subscribeToRefresh((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          resolve(instance(originalRequest));
        });
      });
    }

    return Promise.reject(error);
  }
);

export default instance;
